import React, { useEffect, useState } from 'react';
import logo from '../images/logo.png';
const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);

  // =========================================================
  // COLORS
  // =========================================================
  const colors = {
    white: '#ffffff',
    softWhite: '#f8fbff',
    blue: '#0284c7',
    blueDark: '#0369a1',
    cyan: '#06b6d4',
    navy: '#0f172a',
    slate: '#475569',
    lightBlue: '#e0f2fe',
    lighterBlue: '#f0f9ff',
    border: '#dbeafe',
    borderLight: '#e2e8f0',
  };

  // =========================================================
  // NAVIGATION
  // =========================================================
  const navItems = [
    { label: 'Home', target: 'home' },
    { label: 'About', target: 'About' },
    { label: 'Services', target: 'Services' },
    { label: 'Capabilities', target: 'capabilities' },
    { label: 'Contact', target: 'Contact' },
  ];

  // =========================================================
  // SCROLL EFFECT
  // =========================================================
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);

      const sections = navItems
        .map((item) => document.getElementById(item.target))
        .filter(Boolean);

      let current = 'home';

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();

        if (rect.top <= 150 && rect.bottom >= 150) {
          current = section.id;
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // =========================================================
  // SMOOTH SCROLL
  // =========================================================
  const scrollToSection = (id) => {
    const section = document.getElementById(id);

    if (section) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    setActiveSection(id);
    setIsMobileMenuOpen(false);
  };

  // =========================================================
  // STYLES
  // =========================================================
  const styles = {
    headerWrapper: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 9999,
      padding: isScrolled ? '10px 18px' : '16px 18px',
      transition:
        'padding 0.35s ease, transform 0.35s ease',
      fontFamily:
        '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },

    headerShell: {
      position: 'relative',
      maxWidth: '1380px',
      margin: '0 auto',
      minHeight: '70px',
      padding: '0 14px 0 18px',

      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',

      background: isScrolled
        ? 'rgba(255,255,255,0.92)'
        : 'rgba(255,255,255,0.82)',

      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',

      border: `1px solid ${
        isScrolled
          ? 'rgba(2,132,199,0.20)'
          : 'rgba(226,232,240,0.80)'
      }`,

      borderRadius: '18px',

      boxShadow: isScrolled
        ? '0 15px 45px rgba(15,23,42,0.12)'
        : '0 8px 30px rgba(15,23,42,0.06)',

      transition:
        'all 0.35s cubic-bezier(0.4,0,0.2,1)',

      overflow: 'hidden',
    },

    // =======================================================
    // BLUEPRINT DECORATION
    // =======================================================

    blueprintLine: {
      position: 'absolute',
      left: '0',
      top: '0',
      width: '3px',
      height: '100%',
      background:
        'linear-gradient(180deg, #06b6d4, #0284c7, #0369a1)',
      opacity: 0.9,
    },

    blueprintGlow: {
      position: 'absolute',
      right: '-50px',
      top: '-50px',
      width: '150px',
      height: '150px',
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(14,165,233,0.12), transparent 70%)',
      pointerEvents: 'none',
    },

    // =======================================================
    // BRAND
    // =======================================================

    brandContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '11px',
      cursor: 'pointer',
      textDecoration: 'none',
      position: 'relative',
      zIndex: 2,
      minWidth: '220px',
    },

    logoOuter: {
      width: '46px',
      height: '46px',
      borderRadius: '13px',
      padding: '3px',

      background:
        'linear-gradient(135deg, #0284c7, #06b6d4, #0369a1)',

      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      boxShadow:
        '0 7px 20px rgba(2,132,199,0.22)',

      position: 'relative',

      animation: 'logoFloat 4s ease-in-out infinite',
    },

    logoImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: '10px',
      backgroundColor: '#ffffff',
      padding: '3px',
    },

    // =======================================================
    // BRAND TEXT
    // =======================================================

    brandTextGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      lineHeight: 1,
    },

    companyName: {
      fontSize: '1rem',
      fontWeight: '850',
      color: colors.navy,
      letterSpacing: '-0.035em',
      whiteSpace: 'nowrap',
    },

    companySub: {
      fontSize: '0.61rem',
      fontWeight: '800',
      color: colors.blue,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      marginTop: '3px',
    },

    companyTagline: {
      fontSize: '0.58rem',
      color: '#64748b',
      letterSpacing: '0.02em',
      marginTop: '3px',
      whiteSpace: 'nowrap',
    },

    // =======================================================
    // DESKTOP NAV
    // =======================================================

    navArea: {
      display: 'flex',
      alignItems: 'center',
      gap: '7px',
      position: 'relative',
      zIndex: 2,
    },

    navButton: (isActive, isHovered) => ({
      position: 'relative',
      border: 'none',
      background: 'transparent',
      padding: '10px 13px',

      color:
        isActive || isHovered
          ? colors.blue
          : colors.slate,

      fontSize: '0.86rem',
      fontWeight: isActive ? '700' : '550',

      cursor: 'pointer',

      transition:
        'color 0.25s ease, transform 0.25s ease',

      transform:
        isHovered
          ? 'translateY(-2px)'
          : 'translateY(0)',
    }),

    navUnderline: (isActive, isHovered) => ({
      position: 'absolute',
      bottom: '2px',
      left: '50%',

      width:
        isActive || isHovered
          ? '65%'
          : '0%',

      height: '2px',

      transform: 'translateX(-50%)',

      background:
        'linear-gradient(90deg, #0284c7, #06b6d4)',

      borderRadius: '20px',

      transition:
        'width 0.3s cubic-bezier(0.4,0,0.2,1)',
    }),

    // =======================================================
    // CTA
    // =======================================================

    ctaButton: {
      position: 'relative',
      overflow: 'hidden',

      marginLeft: '10px',

      border: 'none',
      borderRadius: '10px',

      padding: '11px 19px',

      background:
        'linear-gradient(135deg, #0f172a, #1e293b)',

      color: '#ffffff',

      fontSize: '0.82rem',
      fontWeight: '700',

      cursor: 'pointer',

      boxShadow:
        '0 7px 20px rgba(15,23,42,0.18)',

      transition:
        'all 0.3s cubic-bezier(0.4,0,0.2,1)',
    },

    // =======================================================
    // MOBILE BUTTON
    // =======================================================

    mobileButton: {
      display: 'none',
      width: '44px',
      height: '44px',

      border: `1px solid ${colors.borderLight}`,
      backgroundColor: '#ffffff',

      borderRadius: '11px',

      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',

      cursor: 'pointer',

      position: 'relative',
      zIndex: 20,

      transition: 'all 0.3s ease',
    },

    hamburgerBar: (open, index) => ({
      width: '20px',
      height: '2px',
      borderRadius: '10px',
      backgroundColor: colors.navy,

      margin: '2.5px 0',

      transition:
        'all 0.3s cubic-bezier(0.4,0,0.2,1)',

      transform:
        open && index === 0
          ? 'translateY(5px) rotate(45deg)'
          : open && index === 2
            ? 'translateY(-5px) rotate(-45deg)'
            : open && index === 1
              ? 'scaleX(0)'
              : 'none',
    }),
  };

  return (
    <>
      {/* =====================================================
          ANIMATIONS + RESPONSIVE CSS
      ===================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        @keyframes logoFloat {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }

          50% {
            transform: translateY(-3px) rotate(1deg);
          }
        }

        @keyframes menuSlide {
          from {
            opacity: 0;
            transform: translateY(-12px) scale(0.98);
          }

          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes mobileItem {
          from {
            opacity: 0;
            transform: translateX(-15px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulseDot {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }

          50% {
            transform: scale(1.35);
            opacity: 1;
          }
        }

        .header-cta:hover {
          transform: translateY(-2px);
          background: linear-gradient(
            135deg,
            #0284c7,
            #0369a1
          ) !important;

          box-shadow:
            0 10px 25px rgba(2,132,199,0.25) !important;
        }

        .header-cta:active {
          transform: translateY(0) scale(0.97);
        }

        .mobile-nav-item:hover {
          background: #f0f9ff !important;
          color: #0284c7 !important;
          padding-left: 17px !important;
        }

        @media (max-width: 1050px) {

          .desktop-navigation {
            display: none !important;
          }

          .mobile-menu-button {
            display: flex !important;
          }

          .header-shell {
            min-height: 64px !important;
          }

          .brand-container {
            min-width: auto !important;
          }

          .brand-tagline {
            display: none !important;
          }

          .brand-company-name {
            font-size: 0.92rem !important;
          }

          .brand-company-sub {
            font-size: 0.56rem !important;
          }
        }

        @media (max-width: 600px) {

          .header-wrapper {
            padding:
              9px 10px !important;
          }

          .header-shell {
            padding:
              0 9px 0 13px !important;

            border-radius:
              14px !important;
          }

          .logo-outer {
            width: 40px !important;
            height: 40px !important;
            border-radius: 11px !important;
          }

          .brand-company-name {
            font-size: 0.82rem !important;
          }

          .brand-company-sub {
            font-size: 0.49rem !important;
            letter-spacing: 0.13em !important;
          }

          .mobile-drawer {
            left: 10px !important;
            right: 10px !important;
            width: auto !important;
            border-radius: 16px !important;
          }
        }

        @media (max-width: 380px) {

          .brand-company-name {
            font-size: 0.74rem !important;
          }

          .logo-outer {
            width: 37px !important;
            height: 37px !important;
          }

          .mobile-menu-button {
            width: 40px !important;
            height: 40px !important;
          }
        }

      `}</style>

      {/* =====================================================
          HEADER
      ===================================================== */}

      <header
        style={styles.headerWrapper}
        className="header-wrapper"
      >
        <div
          style={styles.headerShell}
          className="header-shell"
        >
          {/* Decorative Engineering Line */}
          <div style={styles.blueprintLine} />

          <div style={styles.blueprintGlow} />

          {/* =================================================
              BRAND
          ================================================= */}

          <div
            style={styles.brandContainer}
            className="brand-container"
            onClick={() => scrollToSection('home')}
          >
            <div
              style={styles.logoOuter}
              className="logo-outer"
            >
              {/* 
                IMPORTANT:
                Replace this path with your actual logo.

                Example:
                /images/sansa-logo.png

                OR:
                import logo from '../Images/logo.png';
                then use src={logo}
              */}

              <img
                src={logo}
                alt="SANSA CONSULTANT Logo"
                style={styles.logoImage}
              />
            </div>

            <div style={styles.brandTextGroup}>
              <span
                style={styles.companyName}
                className="brand-company-name"
              >
                SANSA CONSULTANT
              </span>

              <span
                style={styles.companySub}
                className="brand-company-sub"
              >
                INDIA LLP
              </span>

              <span
                style={styles.companyTagline}
                className="brand-tagline"
              >
                Structural Engineering • Design • Consulting
              </span>
            </div>
          </div>

          {/* =================================================
              DESKTOP NAVIGATION
          ================================================= */}

          <nav
            style={styles.navArea}
            className="desktop-navigation"
          >
            {navItems.map((item, index) => {
              const isActive =
                activeSection === item.target;

              const isHovered =
                hoveredLink === index;

              return (
                <button
                  key={item.target}
                  type="button"
                  style={styles.navButton(
                    isActive,
                    isHovered
                  )}
                  onMouseEnter={() =>
                    setHoveredLink(index)
                  }
                  onMouseLeave={() =>
                    setHoveredLink(null)
                  }
                  onClick={() =>
                    scrollToSection(item.target)
                  }
                >
                  {item.label}

                  <span
                    style={styles.navUnderline(
                      isActive,
                      isHovered
                    )}
                  />
                </button>
              );
            })}

            {/* CTA */}

            <button
              className="header-cta"
              style={styles.ctaButton}
              onClick={() =>
                scrollToSection('Contact')
              }
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                }}
                
              >
                Consult Experts

                <span
                  style={{
                    fontSize: '1rem',
                    transition:
                      'transform 0.3s ease',
                  }}
                >
                  →
                </span>
              </span>
            </button>
          </nav>

          {/* =================================================
              MOBILE MENU BUTTON
          ================================================= */}

          <button
            className="mobile-menu-button"
            style={styles.mobileButton}
            onClick={() =>
              setIsMobileMenuOpen(
                !isMobileMenuOpen
              )
            }
            aria-label="Toggle navigation"
            aria-expanded={isMobileMenuOpen}
          >
            {[0, 1, 2].map((index) => (
              <span
                key={index}
                style={styles.hamburgerBar(
                  isMobileMenuOpen,
                  index
                )}
              />
            ))}
          </button>
        </div>

        {/* ===================================================
            MOBILE DRAWER
        =================================================== */}

        {isMobileMenuOpen && (
          <div
            className="mobile-drawer"
            style={{
              position: 'absolute',
              top: 'calc(100% - 2px)',
              left: '18px',
              right: '18px',

              background:
                'rgba(255,255,255,0.97)',

              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter:
                'blur(20px)',

              border:
                '1px solid rgba(226,232,240,0.9)',

              borderRadius: '18px',

              padding: '12px',

              boxShadow:
                '0 25px 60px rgba(15,23,42,0.16)',

              animation:
                'menuSlide 0.3s ease forwards',

              overflow: 'hidden',
            }}
          >
            {/* Mobile Header */}

            <div
              style={{
                padding:
                  '10px 12px 14px',

                display: 'flex',
                alignItems: 'center',
                gap: '8px',

                borderBottom:
                  `1px solid ${colors.borderLight}`,

                color: colors.slate,

                fontSize: '0.68rem',
                fontWeight: '700',

                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor:
                    colors.cyan,
                  boxShadow:
                    '0 0 10px rgba(6,182,212,0.5)',
                  animation:
                    'pulseDot 2s ease-in-out infinite',
                }}
              />

              Engineering Navigation
            </div>

            {/* Mobile Navigation */}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '8px',
              }}
            >
              {navItems.map(
                (item, index) => {
                  const isActive =
                    activeSection ===
                    item.target;

                  return (
                    <button
                      key={item.target}
                      className="mobile-nav-item"
                      type="button"
                      style={{
                        border: 'none',
                        background:
                          isActive
                            ? colors.lighterBlue
                            : 'transparent',

                        color:
                          isActive
                            ? colors.blue
                            : colors.slate,

                        textAlign: 'left',

                        padding:
                          '12px 13px',

                        borderRadius: '10px',

                        fontSize: '0.95rem',

                        fontWeight:
                          isActive
                            ? '700'
                            : '550',

                        cursor: 'pointer',

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent:
                          'space-between',

                        transition:
                          'all 0.25s ease',

                        animation:
                          `mobileItem 0.35s ease ${
                            index * 0.05
                          }s both`,
                      }}
                      onClick={() =>
                        scrollToSection(
                          item.target
                        )
                      }
                    >
                      <span>
                        {item.label}
                      </span>

                      <span
                        style={{
                          color:
                            isActive
                              ? colors.blue
                              : '#cbd5e1',

                          fontSize:
                            '1rem',

                          transition:
                            'transform 0.25s ease',

                          transform:
                            isActive
                              ? 'translateX(3px)'
                              : 'translateX(0)',
                        }}
                      >
                        →
                      </span>
                    </button>
                  );
                }
              )}
            </div>

            {/* Mobile CTA */}

            <button
              className="header-cta"
              style={{
                ...styles.ctaButton,
                width: '100%',
                marginLeft: 0,
                marginTop: '8px',
                padding: '13px',
              }}
              onClick={() =>
                scrollToSection('contact')
              }
            >
              Consult With Our Engineers →
            </button>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;